(() => {
  if (customElements.get('send-gift-fields')) {
    return;
  }

  class SendGiftFields extends HTMLElement {
    static ACTIVE_CLASS = 'is-open';
    static NOTE_START = '[Send as gift]';
    static NOTE_END = '[/Send as gift]';

    constructor() {
      super();

      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onUpdateField = this.onUpdateField.bind(this);
      this.onClearBtnClick = this.onClearBtnClick.bind(this);
      this.onFieldInput = this.onFieldInput.bind(this);
      this.onRootClickCapture = this.onRootClickCapture.bind(this);
      this.onDetailsToggle = this.onDetailsToggle.bind(this);
      this.onNoteMessageInput = this.onNoteMessageInput.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
    }

    connectedCallback() {
      this.init();
      this.attachEvents();
    }

    disconnectedCallback() {
      this.detachEvents();
    }

    attachEvents() {
      this.debouncedInput = this.debounce(this.onUpdateField, 1000);
      this.debouncedNoteInput = this.debounce(this.onNoteMessageInput, 250);

      this.summaryElement?.addEventListener('click', this.onSummaryClick);
      this.detailsElement?.addEventListener('toggle', this.onDetailsToggle);

      this.fields.forEach((field) => {
        field.addEventListener('input', this.onFieldInput);
        field.addEventListener('input', this.debouncedInput);
      });

      this.noteMessageField?.addEventListener('input', this.debouncedNoteInput);
      this.clearBtn?.addEventListener('click', this.onClearBtnClick);
      this.quickCartRoot?.addEventListener('click', this.onRootClickCapture, true);
      this.addEventListener('focusout', this.onFocusOut);
    }

    detachEvents() {
      this.summaryElement?.removeEventListener('click', this.onSummaryClick);
      this.detailsElement?.removeEventListener('toggle', this.onDetailsToggle);

      this.fields.forEach((field) => {
        field.removeEventListener('input', this.onFieldInput);
        field.removeEventListener('input', this.debouncedInput);
      });

      this.noteMessageField?.removeEventListener('input', this.debouncedNoteInput);
      this.clearBtn?.removeEventListener('click', this.onClearBtnClick);
      this.quickCartRoot?.removeEventListener('click', this.onRootClickCapture, true);
      this.removeEventListener('focusout', this.onFocusOut);
    }

    init() {
      this.detailsElement = this.querySelector('details');
      this.summaryElement = this.detailsElement?.querySelector('summary');
      this.titleText = this.summaryElement?.querySelector('[data-title]');
      this.fields = this.querySelectorAll('[data-field]');
      this.clearBtn = this.querySelector('[data-clear-btn]');

      this.quickCartRoot = this.closest('.quick-cart') || this.closest('form') || document;
      this.cartForm = this.closest('form') || this.quickCartRoot?.closest?.('form') || null;
      this.noteWrapper = this.cartForm?.querySelector?.('[cart-note-input]') || null;
      this.noteMessageField = this.cartForm?.querySelector?.('[data-cart-note-message]') || null;
      this.noteCombinedField = this.cartForm?.querySelector?.('[data-cart-note-combined]') || null;
      this.isSubmitDisabled = false;
      this.isClearingFields = false;
      this.clearOverride = false;
      this.fieldErrorsCommitted = false;

      this.updateSubmitState();
      this.hydrateFromCombinedNote();
    }

    parseCombinedNote(note) {
      const raw = (note || '').toString();
      const start = raw.indexOf(SendGiftFields.NOTE_START);
      const end = raw.indexOf(SendGiftFields.NOTE_END);

      if (start === -1 || end === -1 || end <= start) {
        return { message: raw.trim(), gift: null };
      }

      const message = raw.slice(0, start).trim();
      const giftBlock = raw.slice(start + SendGiftFields.NOTE_START.length, end).trim();

      const gift = {};
      giftBlock.split('\n').forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed) return;

        const parts = trimmed.split(':');
        if (parts.length < 2) return;

        const key = parts.shift().trim().toLowerCase();
        const value = parts.join(':').trim();

        if (key === 'sender phone') gift.sender_phone = value;
        if (key === 'gift message') gift.gift_message = value;
      });

      if (!gift.sender_phone && !gift.gift_message) {
        return { message, gift: null };
      }

      return { message, gift };
    }

    buildCombinedNote() {
      const message = (this.noteMessageField?.value || '').toString().trim();

      const allFieldsFilled = Array.from(this.fields).every((field) => field.value.trim() !== '');
      if (!allFieldsFilled) return message;

      const senderPhone = this.querySelector('[name="sender_phone"]')?.value?.trim?.() || '';
      const giftMessage = this.querySelector('[name="gift_message"]')?.value?.trim?.() || '';

      const lines = [
        message,
        message ? '' : '',
        SendGiftFields.NOTE_START,
        `Sender phone: ${senderPhone}`,
        `Gift message: ${giftMessage}`,
        SendGiftFields.NOTE_END,
      ];

      return lines.filter((l) => l !== '').join('\n');
    }

    hydrateFromCombinedNote() {
      const current = (this.noteCombinedField?.value || this.noteMessageField?.value || '').toString();
      if (!current) return;

      const parsed = this.parseCombinedNote(current);

      if (this.noteMessageField && parsed.message && this.noteMessageField.value.trim() === current.trim()) {
        this.noteMessageField.value = parsed.message;
        this.noteMessageField.textContent = parsed.message;
      }

      if (parsed.gift) {
        const senderPhone = this.querySelector('[name="sender_phone"]');
        const giftMessage = this.querySelector('[name="gift_message"]');

        if (senderPhone && !senderPhone.value) senderPhone.value = parsed.gift.sender_phone || '';
        if (giftMessage && !giftMessage.value) giftMessage.value = parsed.gift.gift_message || '';
      }

      this.syncCombinedNoteField();
      this.updateTitleText();
    }

    getSubmitTargets() {
      return Array.from(
        this.quickCartRoot.querySelectorAll('.quick-cart__submit, .cart__submit')
      );
    }

    getFieldLabel(field) {
      return field?.closest?.('.send-gift-fields__row')?.querySelector?.('label') || null;
    }

    ensureSubmitErrorEl() {
      if (this.submitErrorEl) return this.submitErrorEl;

      const submit = this.getSubmitTargets()[0];
      if (!submit) return null;

      this.submitErrorEl = document.createElement('p');
      this.submitErrorEl.className = 'send-gift-fields__submit-error fs-body-50 t-error hidden';
      this.submitErrorEl.setAttribute('role', 'alert');
      submit.insertAdjacentElement('afterend', this.submitErrorEl);

      return this.submitErrorEl;
    }

    showSubmitError() {
      const el = this.ensureSubmitErrorEl();
      if (!el) return;

      el.textContent = window.theme.strings.cart.send_as_gift_fields_required;
      el.classList.remove('hidden');
    }

    hideSubmitError() {
      this.submitErrorEl?.classList.add('hidden');
    }

    clearFieldErrors() {
      this.fields.forEach((field) => {
        field.classList.remove('t-error');
        this.getFieldLabel(field)?.classList.remove('t-error');
      });

      this.summaryElement?.classList.remove('t-error');
    }

    isFocusOnField() {
      const active = document.activeElement;
      return active && Array.from(this.fields).includes(active);
    }

    commitFieldErrors() {
      const values = Array.from(this.fields).map((field) => field.value.trim());
      const anyFilled = values.some((v) => v !== '');
      const allFilled = values.length > 0 && values.every((v) => v !== '');

      if (anyFilled && !allFilled) {
        this.fieldErrorsCommitted = true;
      }

      this.updateFieldErrors();
    }

    updateFieldErrors() {
      const values = Array.from(this.fields).map((field) => field.value.trim());
      const anyFilled = values.some((v) => v !== '');
      const allFilled = values.length > 0 && values.every((v) => v !== '');
      const allEmpty = values.every((v) => v === '');

      if (allEmpty || allFilled) {
        this.fieldErrorsCommitted = false;
      }

      const shouldError =
        this.fieldErrorsCommitted && anyFilled && !allFilled && !this.isClearingFields;

      this.fields.forEach((field) => {
        const hasError = shouldError && field.value.trim() === '';
        field.classList.toggle('t-error', hasError);
        this.getFieldLabel(field)?.classList.toggle('t-error', hasError);
      });

      const hasFieldErrors = Array.from(this.fields).some((field) =>
        field.classList.contains('t-error')
      );
      this.summaryElement?.classList.toggle('t-error', hasFieldErrors);
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (this.isFocusOnField()) return;

        this.commitFieldErrors();
      }, 0);
    }

    updateTitleText() {
      if (!this.titleText) return;

      const allFieldsFilled = Array.from(this.fields).every((field) => field.value.trim() !== '');
      this.titleText.textContent = allFieldsFilled
        ? window.theme.strings.cart.edit_send_as_gift
        : window.theme.strings.cart.send_as_gift;
    }

    syncCombinedNoteField() {
      if (!this.noteCombinedField) return;
      const next = this.buildCombinedNote();
      this.noteCombinedField.value = next;
      this.noteCombinedField.textContent = next;
    }

    onNoteMessageInput() {
      this.syncCombinedNoteField();
    }

    onSummaryClick(event) {
      const isOpen = this.detailsElement.hasAttribute('open');

      event.preventDefault();

      isOpen
        ? this.close()
        : this.open();
    }

    open() {
      setTimeout(() => {
        this.detailsElement.classList.add(SendGiftFields.ACTIVE_CLASS);
      });

      this.detailsElement.open = true;

      this.summaryElement.setAttribute('aria-expanded', true);

      this.clearOverride = false;
      this.updateSubmitState();
    }

    close() {
      this.detailsElement.classList.remove(SendGiftFields.ACTIVE_CLASS);

      this.summaryElement.setAttribute('aria-expanded', false);

      this.commitFieldErrors();
      this.closeAnimation();

      this.updateSubmitState();
    }

    onDetailsToggle() {
      this.updateSubmitState();
    }

    updateSubmitState() {
      if (!this.detailsElement) return;

      const submitTargets = this.getSubmitTargets();
      if (submitTargets.length === 0) return;

      const isOpen = this.detailsElement.hasAttribute('open');
      const values = Array.from(this.fields).map((field) => field.value.trim());
      const allEmpty = values.length > 0 && values.every((v) => v === '');
      const allFilled = values.length > 0 && values.every((v) => v !== '');

      if (!isOpen) {
        if (allEmpty) {
          this.setSubmitDisabled(submitTargets, false);
        }
        return;
      }

      if (this.clearOverride) return this.setSubmitDisabled(submitTargets, false);
      if (allFilled) return this.setSubmitDisabled(submitTargets, false);
      return this.setSubmitDisabled(submitTargets, true);
    }

    setSubmitDisabled(targets, disabled) {
      this.isSubmitDisabled = disabled;

      if (!disabled) {
        this.hideSubmitError();
      }

      targets.forEach((el) => {
        el.classList.toggle('disabled', disabled);
        el.setAttribute('aria-disabled', disabled ? 'true' : 'false');

        if (disabled) {
          el.setAttribute('disabled', '');
        } else {
          el.removeAttribute('disabled');
        }
      });
    }

    onRootClickCapture(event) {
      if (!this.isSubmitDisabled) return;

      const submitTarget = event.target?.closest?.('.quick-cart__submit');
      if (!submitTarget) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.showSubmitError();
      this.commitFieldErrors();
    }

    onFieldInput() {
      if (this.isClearingFields) {
        this.updateSubmitState();
        return;
      }

      const anyFilled = Array.from(this.fields).some((field) => field.value.trim() !== '');

      if (anyFilled) {
        this.clearOverride = false;
      }

      if (this.fieldErrorsCommitted) {
        this.updateFieldErrors();
      }

      this.updateSubmitState();
    }

    closeAnimation() {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          this.detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }

    onUpdateField() {
      this.updateTitleText();
      this.syncCombinedNoteField();
      this.updateCartNote();
    }

    onClearBtnClick() {
      this.clearOverride = true;
      this.isClearingFields = true;

      this.fields.forEach(field => {
        field.value = ''

        field.dispatchEvent(new Event('input', { bubbles: true }));
      });

      this.isClearingFields = false;
      this.fieldErrorsCommitted = false;
      this.clearFieldErrors();
      this.updateSubmitState();
      this.syncCombinedNoteField();
      this.updateCartNote();
      this.summaryElement.click();
    }

    updateCartNote() {
      const note = this.noteCombinedField?.value ?? this.buildCombinedNote();

      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note }),
      })
        .then((res) => res.json())
        .catch((err) => console.error(err));
    }

    debounce = (fn, wait) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    };
  }

  customElements.define('send-gift-fields', SendGiftFields);
})();

